/** @prettier */

import {useEffect, useState} from 'react';
import {getStatesSites} from '../../API/API_Collection';
import * as apiIF from '../../Type/api';
import * as type from '../../Type/TypeDef';

/**
 * サイトに所属する機器(空調機、給湯器、冷凍機)の稼働台数を取得するフック
 * @param siteProfile {apiIF.resParameters.siteInformations[]} サイトの情報
 *
 * @return viewSiteData {type.mysite.siteData[]} 機器毎の稼働台数
 */
export const useRunCountAllUnit = (
  siteProfile: apiIF.resParameters.siteInformations[] | null
) => {
  /**サイト台数情報*/
  const [viewSiteData, setViewSiteData] = useState<type.mysite.siteData[]>([]);

  //最新サイトデータ取得用
  //サイトデータ取得(消費電力量以外)
  useEffect(() => {
    if (siteProfile === null) return;

    /** サイトID一覧 */
    const siteIds = siteProfile.map(
      (siteInfoData: apiIF.resParameters.siteInformations) => {
        return siteInfoData['site-id'];
      }
    );

    /**
     * サイトユニット稼働状況
     * @param statesSites サイト情報
     * @returns サイトユニット稼働状況
     */
    const calcStatesData = (
      statesSites: apiIF.res.getStatesSites['site-states']
    ): type.mysite.siteData[] => {
      /**
       * 文字列になっている数値を加算し、文字列で返す
       * @param strArray {Array<string | undefined>}
       * @returns 配列内の数字(文字列)を加算して文字列で返す
       */
      const strNumAdd = (strArray: Array<string | undefined>) => {
        let sum = 0;
        for (let strNum of strArray) {
          if (strNum === undefined) continue;
          sum = Number(sum) + Number(strNum);
        }
        return String(sum);
      };

      /**サイトユニット稼働状況情報*/
      const siteUnitData: type.mysite.siteData[] = [];

      for (const siteData of statesSites) {
        const siteID = siteData['site-id'];

        //受信データがない場合は、サイトIDのみ追加して処理終了
        if (siteData.states.length === 0) {
          siteUnitData.push({siteID: siteID});
          continue;
        }

        /** 最新の稼働状況データ (配列の先頭が最新データ) */
        const latestData = siteData.states[0];

        // prettier-ignore
        //取得できない場合0台にする
        siteUnitData.push({
          'siteID'  : siteID,
          'aircon'  : {
            'registerNumber': latestData.airconditioner['register-number'] === undefined ? '0': latestData.airconditioner['register-number'],
            'operatingNumber': latestData.airconditioner['operating-number'] === undefined ? '0': latestData.airconditioner['operating-number'],
            'malfunctioningNumber': latestData.airconditioner['malfunctioning-number'] === undefined ? '0': latestData.airconditioner['malfunctioning-number'],
          },
          'qton'    : {
            'registerNumber': latestData.qton['register-number'] === undefined ? '0': latestData.qton['register-number'],
            'operatingNumber': latestData.qton['operating-number'] === undefined ? '0': latestData.qton['operating-number'],
            'malfunctioningNumber': latestData.qton['malfunctioning-number'] === undefined ? '0': latestData.qton['malfunctioning-number'],
          },
          'freezer' : {
            'registerNumber': strNumAdd([
              latestData.hyozan['register-number'],
              latestData.hccvq['register-number'],
              latestData.chiller['register-number'],
            ]),
            'operatingNumber': strNumAdd([
              latestData.hyozan['operating-number'],
              latestData.hccvq['operating-number'],
              latestData.chiller['operating-number'],
            ]),
            'malfunctioningNumber': strNumAdd([
              latestData.hyozan['malfunctioning-number'],
              latestData.hccvq['malfunctioning-number'],
              latestData.chiller['malfunctioning-number'],
            ]),
          },
        });
      }

      return siteUnitData;
    };

    /**
     * ユニット状態台数取得
     * 定期実行用に処理を切り離しただけ
     */
    function reqStatesSites() {
      //最新データ取得()
      const query: apiIF.reqParameters.getStatesSites = {
        'site-id': siteIds?.join(','),
        'fields': 'register-number,operating-number,malfunctioning-number', //消費電力量以外取得
      };
      getStatesSites(query)
        .then(response => {
          setViewSiteData(calcStatesData(response['site-states']));
        })
        .catch(err => {});
    }

    //ページ遷移時初回読み込み
    reqStatesSites();
    //定期実行
    const intervalId = setInterval(() => {
      reqStatesSites();
    }, 60000);
    return function () {
      clearInterval(intervalId);
    };
  }, [siteProfile]);

  return viewSiteData;
};
